import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ConfiguratorDatastoreService, ConfiguratorEtlService, EtlSetupDTO } from '@tj/tj-dmp-lib-api';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AbstractEtlUpdateComponent } from '../abstract-etl-update.component';
import { Router } from '@angular/router';
import { DataUtilsService } from '../../../../services/data-util.service';
import { AccountService } from '../../../../auth/account.service';
import { isAdminOfTenant } from '../../../../auth/admin-levels';
import { SelectAffectedPartnersComponent } from '../../../../components/select-affected-partners/select-affected-partners.component';

// Extension temporaire de l'interface EtlSetupDTO pour ajouter hasHeaderRow
// En attendant la régénération des fichiers Swagger
interface EtlSetupDTOExtended extends EtlSetupDTO {
    hasHeaderRow?: boolean;
}

@Component({
    selector: 'dmp-etl-update-setup',
    templateUrl: './etl-update-setup.component.html',
    styleUrls: ['./etl-update-setup.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EtlUpdateSetupComponent extends AbstractEtlUpdateComponent implements OnInit {

    form = this.fb.group({
        id: [],
        name: [null, [Validators.required, Validators.min(3)]],
        code: [null, []],
        description: [],
        tenant: [null, [Validators.required]],
        personalDataAccessPartners: [[], [Validators.required]],
        file: [null, [Validators.required]],
        fileContentType: [],
        offset: [0, [Validators.required]],
        separator: [';', [Validators.required]],
        charsetName: ['Cp1252', [Validators.required]],
        tables: [null, [Validators.required]],
        provider: [null],
        type: ['affiliate', [Validators.required]],
        skipPersonal: [false],
        contactType: [''],
        hasHeaderRow: [true, [Validators.required]],
    });
    file: File = null;

    charsetNames: SelectItem[];
    charsetName: SelectItem;
    headerRowOptions: SelectItem[];

    types: SelectItem[] = [];

    tables: Array<string>;

    isCsvFile = false;

    disableTableList = false;

    isAuthorizedAdmin: boolean;
    actualOwner: string;
    affectedPartners: string[] = [];
    /** Partenaire par défaut du tenant courant : toujours sélectionné et non désélectionnable (règle produit) */
    defaultPartnerCode: string = null;

    needContactType = false;
    contactItems: SelectItem[] = [];

    @ViewChild('affectedPartnersSettings') affectedPartnersSettings: SelectAffectedPartnersComponent;

    constructor(
        protected fb: UntypedFormBuilder,
        protected dataUtils: DataUtilsService,
        protected configuratorEtlService: ConfiguratorEtlService,
        protected translate: TranslateService,
        protected router: Router,
        protected confirmationService: ConfirmationService,
        protected messageService: MessageService,
        protected accountService: AccountService,
        protected configuratorDatastoreService: ConfiguratorDatastoreService,
        private cdr: ChangeDetectorRef,
    ) {
        super(router, confirmationService, translate);
        // https://docs.oracle.com/javase/8/docs/technotes/guides/intl/encoding.doc.html
        const list = [
            'Cp1252',           // Windows-1252 (Windows Europe occidentale)
            'UTF8',             // UTF-8 (Standard universel)
            'ISO-8859-1',       // Latin-1 (Europe occidentale)
            'ISO-8859-15',      // Latin-9 (Europe occidentale avec symbole €)
            'UTF-16',           // UTF-16 (Applications Microsoft)
            'UTF-16LE',         // UTF-16 Little Endian (Windows)
            'UTF-16BE',         // UTF-16 Big Endian (Unix/Mac)
            'Cp1250',           // Windows-1250 (Europe centrale: polonais, tchèque, hongrois)
            'Cp1251',           // Windows-1251 (Cyrillique: russe, bulgare, serbe)
            'ISO-8859-2',       // Latin-2 (Europe centrale)
            'ISO-8859-5'        // Cyrillique
        ];
        this.charsetNames = [];
        list.forEach(item => {
            this.charsetNames.push({
                value: item,
                label: item
            });
        });

        // Options pour la ligne d'en-tête
        this.headerRowOptions = [
            {
                label: this.translate.instant('etl.new.setup.header-row.yes'),
                value: true
            },
            {
                label: this.translate.instant('etl.new.setup.header-row.no'),
                value: false
            }
        ];
        this.translate.onLangChange.subscribe(() => {
            this.headerRowOptions = [
                { label: this.translate.instant('etl.new.setup.header-row.yes'), value: true },
                { label: this.translate.instant('etl.new.setup.header-row.no'), value: false }
            ];
        });

        this.updateLang();
        //
    }

    private updateLang(): void {
        ['affiliate', 'network', 'media'].forEach(key => {
            const trad = this.translate.instant('etl.new.setup.types.' + key);
            this.types.push({
                value: key,
                label: trad
            });
        });
    }


    ngOnInit() {
        this.contactItems = [{
            label: this.translate.instant('common.CUSTOMER'),
            value: 'CUSTOMER'
        }, {
            label: this.translate.instant('common.PROSPECT'),
            value: 'PROSPECT'
        }, {
            label: this.translate.instant('common.SUSPECT'),
            value: 'SUSPECT'
        }];
        this.accountService.fetch().subscribe(account => {
            this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
            if (this.id) {
                this.configuratorEtlService.getSetupUsingGET(this.tenant, this.id).subscribe(etlSetup => {
                    this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
                    this.actualOwner = etlSetup.owner;
                    this.affectedPartners = etlSetup.affectedPartners || [];
                    this.updateValue(etlSetup);
                });
            }
            this.form.get('tenant')?.valueChanges.subscribe(tenant => {
                if (tenant != null){
                    this.isAuthorizedAdmin = isAdminOfTenant(account, this.tenant);
                    this.loadDefaultPartner(tenant);
                } else {
                    this.defaultPartnerCode = null;
                }
            });
            if (this.tenant) {
                this.loadDefaultPartner(this.tenant);
            }
        });
    }

    /** Partenaire par défaut du tenant : toujours sélectionné et non désélectionnable dans "Sélection de l'affilié". */
    private loadDefaultPartner(tenant: string) {
        this.configuratorDatastoreService.getDatastoreUsingGET(tenant, true).subscribe(datastore => {
            this.defaultPartnerCode = datastore?.defaultPartner || null;
        });
    }

    updateValue(v: EtlSetupDTOExtended) {
        if (v) {
            this.completChange.emit(v.complet);

            this.needContactType = v.destTable && v.destTable.includes('personal');

            const hasHeaderRowValue = v.hasHeaderRow ?? null;

            if (hasHeaderRowValue !== null) {
                this.form.get('hasHeaderRow').disable();
            } else {
                this.form.get('hasHeaderRow').enable();
            }

            this.form.patchValue({
                id: v.id,
                name: v.name,
                description: v.description,
                tenant: v.tenant,
                personalDataAccessPartners: v.personalDataAccessPartners,
                file: 'non nul',
                fileContentType: v.contentType,
                offset: v.offset,
                separator: v.separator,
                charsetName: v.charsetName,
                tables: v.destTable,
                provider: v.provider,
                type: v.type,
                skipPersonal: v.skipPersonal,
                contactType: v.contactType,
                hasHeaderRow: hasHeaderRowValue
            });
            this.tables = v.destTable;
            if (v.contentType && v.contentType.includes('csv')) {
                this.isCsvFile = true;
            }
            //
            this.disableTableList = this.id != null && this.tables != null && this.tables.length > 0;
        }
    }

    toBase64(file: File): Promise<string>{
        return new Promise((resolve, reject) => {
            this.dataUtils.toBase64(file, (base64String: string) => {
                resolve(base64String);
            });
        });
    }

    setFileData(event: Event, field: string, isImage: boolean): void {
        if (event && event.target && (event.target as any).files && (event.target as any).files[0]) {
            const file = (event.target as any).files[0];
            this.file = file;

            this.toBase64(file).then(base64String => {
                this.form.patchValue({
                [field]: base64String,
                fileContentType: file.type
                });
                this.form.get(field)?.updateValueAndValidity();
            });
        }

    }

    // DEAD CODE (2026-09) : appel à playWithFileUsingPOST déjà commenté ci-dessous ; endpoint
    // confirmé sans appelant vivant dans dmp-ui (voir etl.component.ts#sendEtl).
    sendEtlJob() {
        // let file = this.editForm.get(['file'])!.value;
        // "importmflyflight"
        if (this.form.valid) {
            /* this.engineEtlService.playWithFileUsingPOST(this.tenant, this.etl, "null", null, this.file).subscribe(res => {
              console.log('ok');
              console.log(res)
            }); */
        }
    }

    private getValue(): EtlSetupDTOExtended {
        const v = this.form.getRawValue();
        let owner: string;
        if (this.actualOwner) {
            owner = this.actualOwner;
        } else {
            if (this.isAuthorizedAdmin) {
                owner = 'admin';
            } else {
                owner = v.personalDataAccessPartners;
            }
        }
        const etlSetup =  {
            description: v.description,
            id: v.id,
            personalDataAccessPartners: Array.isArray(v.personalDataAccessPartners) ?
                v.personalDataAccessPartners :
                (v.personalDataAccessPartners ? [v.personalDataAccessPartners] : []),
            owner,
            name: v.name,
            offset: v.offset,
            separator: v.separator,
            loadType: 'SQL',
            contentType: this.file ? this.file.type : null,
            tenant: v.tenant,
            destTable: v.tables,
            charsetName: v.charsetName,
            provider: v.provider,
            type: v.type,
            skipPersonal: v.skipPersonal,
            affectedPartners: this.affectedPartners ?? [],
            contactType: v.contactType,
            hasHeaderRow: v.hasHeaderRow
        } as EtlSetupDTOExtended;
        return etlSetup;
    }

    save() {
        if (this.form.valid) {
            // persit le tenant
            const tenant = this.formControls.tenant.value;
            //
            const etlSetup: EtlSetupDTOExtended = this.getValue();

            this.configuratorEtlService.updateSetupUsingPOST(etlSetup.tenant, etlSetup).subscribe(uuid => {
                if (uuid) {
                    if (this.file) {
                        this.configuratorEtlService.addSetupFileUsingPOST(etlSetup.tenant, this.file, uuid).subscribe(res => {
                            this.next(uuid, tenant, this.isAuthorizedAdmin ? null : etlSetup.personalDataAccessPartners[0]);
                        });
                    } else {
                        this.next(uuid, tenant, this.isAuthorizedAdmin ? null : etlSetup.personalDataAccessPartners[0]);
                    }
                } else {
                    console.log('nok');
                }
            });
            /* this.configuratorValidatorService.isExistUsingPOST('etl', etlSetup.tenant, 'name', etlSetup.name)
                 .subscribe(result => {
                     if (result) {
                         this.messageService.add({severity: 'error', summary: 'Nom', detail: 'Existe déjà', key: 'popin'});
                     } else {

                     }
                 });*/
        }
    }

    next(id: string = this.id, tenant: string, partner: string) {
        // Le nom part avec l'événement : le titre de l'assistant le reprend (création comme modification).
        this.nextStep.emit({uuid: id, tenant, partner, name: this.formControls.name.value});
    }

    tablesDestChange(tables: Array<string>) {
        this.tables = tables;
        this.form.patchValue({
            tables
        });
        if (tables.includes('personal')){
            this.needContactType = true;
            this.form.get('contactType').setValidators(Validators.required);
            this.cdr.detectChanges();

        } else {
            this.needContactType = false;
            this.form.get('contactType').setValidators([]);
            this.cdr.detectChanges();
        }
    }

    get formControls() {
        return this.form.controls;
    }

    updateSkipPersonal(skipPersonal: boolean) {
        this.form.patchValue({
            skipPersonal
        });
    }

    /**
     * Gérer la mise à jour des affiliés affectés
     * @param affectedPartners Liste de string contenant le code des affiliés affectés
     */
    updateAffectedPartners(affectedPartners: string[]) {
        this.affectedPartners = affectedPartners;
    }
}
